/**
 * SEO Component - React 19 native metadata support
 *
 * React 19 automatically hoists <title>, <meta>, and <link> tags from JSX
 * into <head>. No third-party library needed. Just render the tags inline.
 */

const DEFAULT_TITLE =
  'Best Travel Agency in Kashmir | Best Kashmir Tour Packages - Velmora Holidays';
const DEFAULT_DESCRIPTION =
  'Best travel agency in Kashmir offering the best Kashmir tour packages. Expert guides, affordable prices, customized Kashmir holiday packages. Book Gulmarg, Pahalgam, Sonamarg tours.';
const SITE = 'https://velmoraholidays.com';
const SITE_TITLE = 'Velmora Holidays';

export const SEO = ({
  title,
  description,
  canonical,
  type = 'website',
  image = `${SITE}/logo.png`,
  keywords,
  noindex = false,
}) => {
  const fullTitle = title
    ? (title.includes(SITE_TITLE) ? title : `${title} | ${SITE_TITLE}`)
    : DEFAULT_TITLE;
  const metaDescription = description || DEFAULT_DESCRIPTION;
  const canonicalUrl = canonical ? `${SITE}${canonical}` : SITE;

  return (
    <>
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={metaDescription} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonicalUrl} />
      <meta
        name="robots"
        content={
          noindex
            ? 'noindex, nofollow'
            : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
        }
      />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={image} />
      <meta
        property="og:site_name"
        content="Velmora Holidays - Best Travel Agency in Kashmir"
      />
      <meta property="og:locale" content="en_IN" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={image} />
    </>
  );
};

/**
 * ArticleSEO - Blog post metadata (React 19 native).
 */
export const ArticleSEO = ({
  title,
  description,
  canonical,
  image,
  author = 'Velmora Holidays',
  publishedTime,
  modifiedTime,
  category,
}) => {
  const fullTitle = `${title} | Kashmir Travel Blog - ${SITE_TITLE}`;
  const canonicalUrl = `${SITE}${canonical}`;

  return (
    <>
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <meta name="author" content={author} />
      <link rel="canonical" href={canonicalUrl} />
      <meta
        name="robots"
        content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
      />

      <meta property="og:type" content="article" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      {image && <meta property="og:image" content={image} />}
      <meta property="og:site_name" content="Velmora Holidays" />
      <meta property="article:author" content={author} />
      {publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {category && <meta property="article:section" content={category} />}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      {image && <meta name="twitter:image" content={image} />}
    </>
  );
};

/**
 * ProductSEO - Tour package metadata (React 19 native).
 */
export const ProductSEO = ({
  name,
  description,
  canonical,
  image,
  price,
  availability = 'InStock',
}) => {
  const fullTitle = `${name} | Kashmir Tour Package - ${SITE_TITLE}`;
  const canonicalUrl = `${SITE}${canonical}`;

  return (
    <>
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      <meta
        name="robots"
        content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
      />

      <meta property="og:type" content="product" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      {image && <meta property="og:image" content={image} />}
      <meta property="og:site_name" content="Velmora Holidays" />
      {price && (
        <meta
          property="product:price:amount"
          content={String(price).replace('₹', '').replace(',', '')}
        />
      )}
      <meta property="product:price:currency" content="INR" />
      <meta property="product:availability" content={availability} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      {image && <meta name="twitter:image" content={image} />}
    </>
  );
};

export default SEO;
